/**
 * Calendar days and times in a named timezone, for what a person means by a date (BUILD_SPEC §13.1): a day starts at
 * its midnight where they are, a week on the workspace's first day, daylight saving included. Intl alone, as the
 * browser and the Worker both have it.
 */

const formats = new Map<string, Intl.DateTimeFormat>();

function formatFor(timeZone: string): Intl.DateTimeFormat {
  let format = formats.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formats.set(timeZone, format);
  }
  return format;
}

/** Whether a timezone's name is one Intl knows: "Europe/London", "UTC". */
export function isTimezone(name: string): boolean {
  try {
    formatFor(name);
    return true;
  } catch {
    return false;
  }
}

/** The calendar day and time an instant is, where `timeZone` is. */
function partsAt(instant: number, timeZone: string) {
  const parts = Object.fromEntries(
    formatFor(timeZone)
      .formatToParts(new Date(instant))
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** How far ahead of UTC `timeZone` is at an instant, in milliseconds. */
function offsetAt(instant: number, timeZone: string): number {
  const p = partsAt(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/** The calendar day, `YYYY-MM-DD`, an instant falls on where `timeZone` is. */
export function dayAt(instant: number, timeZone: string): string {
  const p = partsAt(instant, timeZone);
  return `${String(p.year).padStart(4, "0")}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/**
 * The instant a calendar day's time is where `timeZone` is: its midnight by default. A time a clock skips, as one
 * springing forward does, is the instant just after the gap.
 */
export function instantAt(day: string, timeZone: string, time = "00:00"): number {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  const [hour, minute] = time.split(":").map(Number) as [number, number];
  const wall = Date.UTC(year, month - 1, date, hour, minute);
  // The offset at the wall time read as UTC, then again at the instant that gives, settles across a change of offset.
  const first = wall - offsetAt(wall, timeZone);
  return wall - offsetAt(first, timeZone);
}

/** A calendar day `days` later, or earlier for a negative count. */
export function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** How many days from one calendar day to another: fewer than none where it is before. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** A calendar day `months` later, or earlier, its day of the month kept where the month has it, else its last. */
export function addMonths(day: string, months: number): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  first.setUTCDate(Math.min(date, last));
  return first.toISOString().slice(0, 10);
}

/** The first day of the week holding `day`, the week starting on `weekStart` (0 Sunday … 6 Saturday). */
export function weekStartOf(day: string, weekStart: number): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, -((weekday - weekStart + 7) % 7));
}

/** The first day of the month holding `day`. */
export function monthStartOf(day: string): string {
  return `${day.slice(0, 8)}01`;
}

/** Whether text is a real calendar day, `YYYY-MM-DD`: no 31st of April. */
export function isDay(text: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const parsed = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === text;
}
